import * as React from 'react';
import classnames from 'classnames';
import { Tooltip, Input, Divider } from 'antd';
import { EditOutlined, CloseOutlined } from '@ant-design/icons';
import debounce from 'lodash/debounce';
import merge from 'lodash/merge';
import { produce } from 'immer';
import { outsidePopupClassName } from '../../../utils';
import { getRuleValuesTitle } from './utils';
import { Select } from './styled';
import * as Styled from './styled';
import type { SelectProps, RefSelectProps } from 'antd';
import type * as Types from '../../../../types';
import styles from './style.less';

export interface RuleValueSelectProps extends SelectProps<Types.FilterRuleValuePair[]> {
  valueBackfill?: boolean;
  valueEditable?: boolean;
  onChange?: (value: Types.FilterRuleValuePair[]) => void;
}

function getInnerSelectValue(value: RuleValueSelectProps['value'], multiple?: boolean) {
  // Empty value
  if (value?.length === 1 && value[0].display === '' && value[0].value === '') return undefined;

  if (!multiple) return value?.[0] ? { value: value[0].value, label: value[0].display } : undefined;

  return value?.map((it) => ({ value: it.value, label: it.display }));
}

const RuleValueSelect = React.forwardRef<RefSelectProps, RuleValueSelectProps>((props, ref) => {
  const {
    valueBackfill = true,
    valueEditable = false,
    options,
    onChange,
    onSearch,
    onBlur,
    ...params
  } = props;
  const multiple = props.mode === 'multiple' || props.mode === 'tags';
  const innerRef = React.useRef<RefSelectProps>(null);
  const [value, setValue] = React.useState(() =>
    getInnerSelectValue(props.value || props.defaultValue, multiple),
  );
  const [editItem, setEditItem] = React.useState<string | number>();

  /**
   * Shared handler for committing an inline value edit (pencil icon) in multiple mode.
   * Used by both Enter key and blur so clicking outside behaves the same as pressing Enter.
   */
  const commitInlineEdit = (
    inputValue: string,
    currentItemValue: string | number,
    itemIndex: number,
  ) => {
    if (!inputValue || inputValue === String(currentItemValue)) {
      setEditItem(undefined);
      return;
    }

    // Silently reject if the new value already exists in the list
    if (Array.isArray(value) && value.findIndex((item) => inputValue === item.value) !== -1) {
      return;
    }

    setValue((currentValue) => {
      if (!Array.isArray(currentValue)) return currentValue;
      return produce(currentValue, (draft) => {
        draft[itemIndex] = { value: inputValue, label: inputValue };
      });
    });
    setEditItem(undefined);
    innerRef.current?.focus();
  };

  React.useImperativeHandle(ref, () => innerRef.current as RefSelectProps, []);

  React.useEffect(() => {
    innerRef.current?.focus();
  }, []);

  React.useEffect(() => {
    setValue(getInnerSelectValue(props.value, multiple));
    setEditItem(undefined);
  }, [props.value, multiple]);

  const selectOptions = (() => {
    const res = [...(options || [])] as {
      value: string | number;
      label: string | React.ReactNode;
    }[];

    if (valueBackfill && !res.length) {
      const existingValues = Array.isArray(value) ? value : value ? [value] : [];

      if (valueEditable) {
        const editableItems = existingValues.map((it, ix) => ({
          ...it,
          label: (
            <Styled.EditableSelectOption onClick={(e) => e.stopPropagation()}>
              {editItem === it.value ? (
                <Input
                  autoFocus
                  defaultValue={it.value}
                  size="small"
                  onBlur={(e) => {
                    if (!multiple) {
                      setEditItem(undefined);
                      return;
                    }
                    const target = e.target as HTMLInputElement;
                    commitInlineEdit(target.value, it.value, ix);
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  onKeyDown={(e) => {
                    e.stopPropagation();
                    const target = e.target as HTMLInputElement;

                    if (e.key !== 'Enter' || !target.value) {
                      return;
                    }

                    if (!multiple) {
                      onChange?.([{ value: target.value, display: target.value }]);
                      setEditItem(undefined);
                      return;
                    }

                    commitInlineEdit(target.value, it.value, ix);
                  }}
                />
              ) : (
                <>
                  <Styled.EditableSelectOptionLabel title={it.label}>
                    {it.label}
                  </Styled.EditableSelectOptionLabel>
                  <EditOutlined
                    onClick={() => {
                      setEditItem(it.value);
                    }}
                  />
                  <Divider type="vertical" />
                  <CloseOutlined
                    onClick={(e) => {
                      if (!multiple) {
                        onChange?.([{ value: '', display: '' }]);

                        return;
                      }

                      const target = (e.target as HTMLElement).closest('.ant-select-item');

                      if (target) {
                        (target as HTMLDivElement).click();
                      }
                    }}
                  />
                </>
              )}
            </Styled.EditableSelectOption>
          ),
        }));

        res.push(...editableItems);
      } else {
        res.push(...existingValues);
      }
    }

    return res;
  })();

  const debouncedHandleSearch = React.useMemo(
    () => debounce((v) => onSearch?.(v), 300),
    [onSearch],
  );

  return (
    <Select<any>
      defaultOpen
      variant="borderless"
      maxTagCount={0}
      maxTagTextLength={20}
      maxTagPlaceholder={(v) => <Tooltip title={getRuleValuesTitle(v)}>+ {v.length} ...</Tooltip>}
      {...params}
      ref={innerRef}
      labelInValue
      options={selectOptions}
      value={value}
      popupMatchSelectWidth={236}
      classNames={merge({}, params.classNames, {
        popup: {
          root: classnames(
            outsidePopupClassName,
            valueEditable && styles.editableSelectDropdown,
            params.classNames?.popup?.root,
          ),
        },
      })}
      onSearch={debouncedHandleSearch}
      onChange={(v) => {
        if (multiple) {
          return setValue(
            v.map((it) => ({
              value: it.value,
              label: typeof it.label === 'object' ? it.value : it.label || it.value,
            })),
          );
        }

        onChange?.([
          {
            value: v.value,
            display: typeof v.label === 'object' ? v.value : v.label || v.value,
          },
        ]);
      }}
      onBlur={(e) => {
        if (editItem !== undefined) {
          return;
        }

        onBlur?.(e);

        if (multiple) {
          if (!Array.isArray(value)) return;

          if (!value.length) {
            onChange?.([{ value: '', display: '' }]);
            return;
          }

          onChange?.(value.map((it) => ({ value: it.value, display: it.label })));
        }
      }}
      onInputKeyDown={(e) => {
        const target = e.target as HTMLInputElement;

        if (e.key === 'Enter' && !target.value) {
          e.preventDefault();
          e.stopPropagation();
        }

        if (e.key === 'Tab' && target.value) {
          e.preventDefault();
          e.stopPropagation();

          target.dispatchEvent(
            new KeyboardEvent('keydown', {
              key: 'Enter',
              code: 'Enter',
              keyCode: 13,
              bubbles: true,
              cancelable: true,
            }),
          );
        }
      }}
    />
  );
});

RuleValueSelect.displayName = 'RuleValueSelect';

export default RuleValueSelect;
